
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from '@/stores/authStore.js'
import { userApi } from '@/api/User/UserApi.js'
import { useBroadcastChannel } from '@vueuse/core'

/**
 * Session Timeout Warning Modal
 * Initiates an interval based on the user service timeout setting. When warning time is reached modal shows asking if user wishes to continue.
 * 	Selecting Yes does request to user service which resets the session timer. No logs the user out. If session times out, the session expired modal will show.
 * @param {{ sessionDuration: number, buffer: number, warningTime: number }} options Times in seconds to override defaults. SessionDuration should be set to value in authStore.
 * @returns {{showWarning: boolean, showExpired: boolean, timeRemaining: number, resetTimer: function,handleLogout: function}}
 */
export function useSessionTimeout(options = {})
{
	let {
		//Minutes
		sessionDuration = 30, // 30 minutes default (subtracted by buffer) Set in SessionTimeoutModal
		buffer = 1.2, // Subtracted from sessionDuration, buffer so user continuing session isn't racing timeout at last second and to avoid the auth request caching (1 minute).
		warningTime = 5, // 5 minutes before expiry
	} = options

	//Convert values to milliseconds
	sessionDuration = sessionDuration * 60 * 1000
	buffer = buffer * 60 * 1000
	warningTime = warningTime * 60 * 1000

	const channel = useBroadcastChannel({ name: 'ApiGateway' })

	const showWarning = ref(false);
	const showExpired = ref(false);
	const timeRemaining = ref(sessionDuration);

	const authStore = useAuthStore();

	let warningTimer = null;
	let expireTimer = null;
	let countdownInterval = null;

	/**
	 * Start the timer that shows modal if warning time reached.
	 */
	const startTimer = () =>
	{
		clearTimers();

		if (warningTime > sessionDuration - buffer) console.warn("Session warning time exceeds session duration.")

		// Set warning timer
		warningTimer = setTimeout(() =>
		{
			showWarning.value = true
			startCountdown()
		}, sessionDuration - buffer - warningTime);

		// Set expiration timer
		expireTimer = setTimeout(() =>
		{
			handleExpire();
		}, sessionDuration - buffer);
	}

	/**
	 * Start the countdown of the remaining time that is shown in the dialog
	 */
	const startCountdown = () =>
	{
		timeRemaining.value = warningTime
		countdownInterval = setInterval(() =>
		{
			timeRemaining.value -= 1000

			if (timeRemaining.value <= 0)
			{
				clearInterval(countdownInterval)
			}
		}, 1000)
	}

	/**
	 * Reset the timer
	 */
	const resetTimer = (forceUserDataRefresh = false) =>
	{
		if (authStore.isLoggedIn)
		{
			if (forceUserDataRefresh) {
				userApi.refreshUserData();
			}
			else {
				//This calls a special api.get_for_reset function so ApiGateway doesn't get in an infinite loop
				userApi.throttledRefreshUserData();
			}
		}

		showWarning.value = false;
		startTimer();
	}

	/**
	 * Called when expiration occurs
	 */
	const handleExpire = () =>
	{
		showWarning.value = false;
		clearTimers();
		showExpired.value = true;
	}

	/**
	 * Logs the user out when selects No to continuing session
	 */
	const handleLogout = () =>
	{
		authStore.logout(true);
	}

	/**
	 * Clear timers and interval if active
	 */
	const clearTimers = () =>
	{
		if (warningTimer) clearTimeout(warningTimer);
		if (expireTimer) clearTimeout(expireTimer);
		if (countdownInterval) clearInterval(countdownInterval);
	}

	/**
	 * Start timer when layout is mounted
	 */
	onMounted(() =>
	{
		watch(
			channel.data,
			() => {
				resetTimer();
			});

		resetTimer();
	})

	/**
	 * Stop timers and clear interval when layout is unmounted
	 */
	onUnmounted(() =>
	{
		clearTimers();
	})

	return {
		showWarning,
		showExpired,
		timeRemaining,
		resetTimer,
		handleLogout
	}
}